import React, { useRef, useState, useEffect, useMemo, useCallback } from "react";
import Map, { Source, Layer, NavigationControl, Popup } from "react-map-gl";
import axios from "axios";
import {
  Box,
  Typography,
  List,
  ListItem,
  Radio,
  RadioGroup,
  FormControl,
  FormControlLabel,
  Tooltip,
  CircularProgress,
  Select,
  MenuItem,
  Card,
  CardContent,
  CardActions,
  Button,
  Grid,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import {
  commodities,
  railDwellTimes,
  airportDelay,
  countrywideAirportDelay,
} from "../../common/constants";
import MapSettings from "./MapSettings";
import LayerControls from "./LayerControls";
import MapLegend, { POINT_LAYERS } from "./MapLegend";
import GroupedBarChart from "../Highcharts/GroupedBarChart";
import PieChart from "../Highcharts/PieChart";

/* ------------------------------------------------------------------ */
/* Constants                                                           */
/* ------------------------------------------------------------------ */

const TEXAS_VIEW = { longitude: -99.9018, latitude: 30.5, zoom: 5.5, pitch: 50 };
const US_VIEW = { longitude: -95.7129, latitude: 37.0702, zoom: 3.9, pitch: 10 };

const EMPTY_FC = { type: "FeatureCollection", features: [] };

const INITIAL_MAP_PARAMETERS = {
  roadwayPerformance: "trkdelay",
  commodities: "Route_tons",
  selectedCommoditiesCategory: "Route_tons",
  colorScheme: ["#2b83ba", "#ffffbf", "#d7191c"],
  barHeight: "commodities",
  barColor: "roadwayPerformance",
  barOpacity: 0.85,
  showRoads: "visible",
  showCargoAirports: "none",
  showPorts: "none",
  showTruckParking: "none",
  showRailDwellTimes: "none",
  showAirportDelay: "none",
  showCountrywideAirportDelay: "none",
  showDelayLines: "none",
  showAisData2021: "none",
  baseMap: "mapbox/streets-v11",
};

const CORRIDOR_SERVICE_URL =
  "https://services6.arcgis.com/1UWmeVkkResmC9lR/ArcGIS/rest/services/Texs_Network_Performance_2015_Commodities_Buffered/FeatureServer/0/query";

const CORRIDOR_FIELDS = [
  "RIA_RTE_ID", "FRM_DFO", "TO_DFO", "trkdelay", "trkpti95",
  "PoultryTon", "CotGraiTon", "ProduceTon", "EnergyTon", "ElectroTon", "AutoTon",
  "PoultValue", "CotGraValu", "ProduValue", "EnergValue", "ElectValue", "AutoValue",
  "Route_tons", "Route_valu",
];

const DISTRICT_IDS = Array.from({ length: 25 }, (_, i) => i + 1);
const COUNTY_IDS = Array.from({ length: 254 }, (_, i) => i + 1);

const CARGO_AIRPORTS_URL =
  "https://geo.dot.gov/mapping/rest/services/NTAD/Aviation_Facilities/MapServer/0/query?" +
  new URLSearchParams({
    where: "OTHER_SERVICES like '%CARGO%' and STATE_CODE = 'TX'",
    outFields: "*",
    returnGeometry: "true",
    f: "geojson",
  });

const PORTS_URL =
  "https://opendata.arcgis.com/datasets/595556c111e14519be70b955e10aa8ff_0.geojson";

const TRUCK_PARKING_URL =
  "https://tti-txcat-api-dev.azurewebsites.net/api/poi/getTruckParkingLocations?counties=" +
  COUNTY_IDS.join(",");

const AIS_TILES_URL =
  "https://coast.noaa.gov/arcgis/rest/services/MarineCadastre/AISVesselTransitCounts2021/MapServer/tile/{z}/{y}/{x}";

const RAIL_X_CATEGORIES = [
  "1/6/2023", "1/13/2023", "1/20/2023", "1/27/2023", "2/3/2023", "2/10/2023", "2/17/2023",
  "2/24/2023", "3/3/2023", "3/10/2023", "3/17/2023", "3/24/2023", "3/31/2023",
];

const INTERACTIVE_LAYER_IDS = [
  "comm-corridor-layer",
  "cargo-airports-layer",
  "railDwellTimes-layer",
  "airportDelay-layer",
  "countrywideAirportDelay-layer",
];

// Sources that show a hover label and open a detail popup on click
const POPUP_SOURCES = {
  railDwellTime: "Rail Dwell Times",
  airportDelay: "Airport Delay",
  countrywideAirportDelay: "Countrywide Airport Delay",
};

const DARK_TOOLTIP_PROPS = {
  tooltip: {
    sx: {
      bgcolor: "common.black",
      "& .MuiTooltip-arrow": { color: "common.black" },
      maxWidth: 300,
    },
  },
};

/* ------------------------------------------------------------------ */
/* Static GeoJSON built from constants                                 */
/* ------------------------------------------------------------------ */

const pointFeature = (coordinates, properties) => ({
  type: "Feature",
  geometry: { type: "Point", coordinates },
  properties,
});

const RAIL_DWELL_DATA = {
  type: "FeatureCollection",
  features: railDwellTimes.map(({ id, name, averageTerminalDwellTimeHours, coordinates }) =>
    pointFeature(coordinates, { id, name, averageTerminalDwellTimeHours })
  ),
};

const AIRPORT_DELAY_DATA = {
  type: "FeatureCollection",
  features: airportDelay.map(
    ({ id, name, averageArrrivalsDelayMinutes, averageDeparturesDelayMinutes, coordinates }) =>
      pointFeature(coordinates, { id, name, averageArrrivalsDelayMinutes, averageDeparturesDelayMinutes })
  ),
};

const COUNTRYWIDE_AIRPORT_DATA = {
  type: "FeatureCollection",
  features: countrywideAirportDelay.map((obj) =>
    pointFeature([obj.long, obj.lat], {
      id: obj.id,
      name: obj.name,
      avgArrivalsDelay: obj.arrivals.averageDelay,
      avgDeparturesDelay: obj.departures.averageDelay,
    })
  ),
};

// Lines from the origin airport to every countrywide destination
const DELAY_LINES_DATA = {
  type: "FeatureCollection",
  features: COUNTRYWIDE_AIRPORT_DATA.features.map((f) => ({
    type: "Feature",
    properties: f.properties,
    geometry: {
      type: "LineString",
      coordinates: [airportDelay[0].coordinates, f.geometry.coordinates],
    },
  })),
};

/* ------------------------------------------------------------------ */
/* Layer styles                                                        */
/* ------------------------------------------------------------------ */

const symbolLayer = (id, imageId, visibility, size = 1, allowOverlap = false) => ({
  id,
  type: "symbol",
  layout: {
    visibility,
    "icon-image": imageId,
    "icon-size": size,
    "icon-allow-overlap": allowOverlap,
  },
});

const DELAY_LINES_LAYER = {
  id: "polylineLayer",
  type: "line",
  layout: { "line-join": "round", "line-cap": "round" },
  paint: {
    "line-color": [
      "interpolate", ["linear"], ["get", "avgArrivalsDelay"],
      20, "rgb(122, 193, 67)",
      80, "rgb(237, 28, 36)",
    ],
    "line-width": [
      "interpolate", ["linear"], ["get", "avgDeparturesDelay"],
      20, 5,
      80, 35,
    ],
    "line-opacity": 0.8,
  },
};

const AIS_LAYER = { id: "ais-2021-data", type: "raster" };

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

const getRange = (features, field) => {
  let min = Infinity;
  let max = -Infinity;
  features.forEach(({ properties }) => {
    const v = properties[field];
    if (Number.isFinite(v)) {
      min = Math.min(min, v);
      max = Math.max(max, v);
    }
  });
  return Number.isFinite(min) ? { min, max } : { min: 0, max: 0 };
};

const buildCommodityDetails = (features) => {
  const delay = getRange(features, "trkdelay");
  const pti = getRange(features, "trkpti95");

  return commodities.map((item) => {
    const tons = getRange(features, item.variables.tons);
    const values = getRange(features, item.variables.values);
    return {
      ...item,
      minCommodity: { tons: tons.min, values: values.min },
      maxCommodity: { tons: tons.max, values: values.max },
      minRoadPerf: { trkdelay: delay.min, trkpti95: pti.min },
      maxRoadPerf: { trkdelay: delay.max, trkpti95: pti.max },
    };
  });
};

// Mapbox "interpolate" needs strictly increasing stops
const safeRange = ({ field, min, max }) => ({
  field,
  min,
  max: max > min ? max : min + 1,
});

const getCommodityField = (groupName, category) => {
  const group = commodities.find((c) => c.name === groupName);
  return category === "Route_valu" ? group.variables.values : group.variables.tons;
};

const fetchCorridorFeatures = async () => {
  const requests = DISTRICT_IDS.map((id) =>
    axios.get(CORRIDOR_SERVICE_URL, {
      params: {
        where: `Di=${id}`,
        outFields: CORRIDOR_FIELDS.join(","),
        returnGeometry: true,
        returnExceededLimitFeatures: true,
        f: "pgeojson",
      },
    })
  );

  const results = await Promise.allSettled(requests);
  return results.flatMap((result, i) => {
    if (result.status === "fulfilled") return result.value.data.features ?? [];
    console.error(`Failed to load district ${DISTRICT_IDS[i]}`, result.reason);
    return [];
  });
};

const getRailSeries = (id) => {
  const station = railDwellTimes.find((r) => r.id === id);
  return station ? [{ name: station.name, data: station.data }] : [];
};

const getAirportDelayChart = (id) => {
  const monthlyData = airportDelay.find((a) => a.id === id)?.monthlyData ?? [];
  return {
    categories: monthlyData.map((m) => m.month),
    series: [
      { name: "Arrivals", data: monthlyData.map((m) => m.averageArrrivalsDelayMinutes) },
      { name: "Departures", data: monthlyData.map((m) => m.averageDeparturesDelayMinutes) },
    ],
  };
};

const formatNumber = (val, prefix = "") =>
  val === null || val === undefined || isNaN(val)
    ? "N/A"
    : prefix + Math.round(val).toLocaleString();

/* ------------------------------------------------------------------ */
/* Hooks                                                               */
/* ------------------------------------------------------------------ */

// Fetches GeoJSON the first time a layer is turned on, then keeps it
const useLazyGeoJson = (url, enabled) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!enabled || data) return undefined;
    let cancelled = false;
    axios
      .get(url)
      .then((res) => !cancelled && setData(res.data))
      .catch((err) => console.error(`Failed to load ${url}`, err));
    return () => {
      cancelled = true;
    };
  }, [url, enabled, data]);

  return data;
};

/* ------------------------------------------------------------------ */
/* Small components                                                    */
/* ------------------------------------------------------------------ */

const RouteTooltip = ({ hoverInfo, mapRef }) => {
  if (!hoverInfo) return null;

  const { feature, x, y } = hoverInfo;
  const p = feature.properties;

  const container = mapRef.current?.getContainer();
  const mapWidth = container?.clientWidth ?? 0;
  const mapHeight = container?.clientHeight ?? 0;
  const width = 300;
  const height = 190;
  const offset = 15;

  const left = x + offset + width > mapWidth ? x - width - offset : x + offset;
  const top = y + offset + height > mapHeight ? y - height - offset : y + offset;

  return (
    <Box
      sx={{
        position: "absolute",
        left,
        top,
        width,
        zIndex: 10,
        pointerEvents: "none",
        bgcolor: "rgba(255, 255, 255, 0.95)",
        borderRadius: 1,
        boxShadow: 3,
        p: 1.5,
      }}
    >
      <Typography sx={{ fontSize: 14, fontWeight: "bold", borderBottom: 1, mb: 0.5 }}>
        Route Information
      </Typography>
      <Typography sx={{ fontSize: 13 }}>
        <strong>Route ID:</strong> {p.RIA_RTE_ID}
      </Typography>
      <Typography sx={{ fontSize: 13 }}>
        <strong>Commodity Tons (annual):</strong> {formatNumber(p.Route_tons)}
      </Typography>
      <Typography sx={{ fontSize: 13 }}>
        <strong>Commodity Value (annual):</strong> {formatNumber(p.Route_valu, "$")}
      </Typography>
      <Typography sx={{ fontSize: 13 }}>
        <strong>Truck Delay:</strong> {formatNumber(p.trkdelay)} annual person-hours
      </Typography>
      <Typography sx={{ fontSize: 13 }}>
        <strong>Truck Reliability (PTI-95):</strong>{" "}
        {p.trkpti95 != null ? Number(p.trkpti95).toFixed(2) : "N/A"}
      </Typography>
      <Typography sx={{ fontSize: 11, color: "text.secondary", mt: 0.5 }}>
        Annual commodity tons and values are from 2015 TRANSEARCH data.
      </Typography>
    </Box>
  );
};

const DetailRow = ({ label, children }) => (
  <Typography sx={{ fontSize: 14 }} color="text.secondary">
    <strong>{label}:</strong> {children}
  </Typography>
);

const DetailPopup = ({ selected, title, onClose, chartRef, children }) => {
  // Highcharts needs a reflow once the popup has its final size
  useEffect(() => {
    const timer = setTimeout(() => chartRef.current?.chart?.reflow(), 300);
    return () => clearTimeout(timer);
  }, [chartRef, selected]);

  return (
    <Popup
      longitude={selected.lng}
      latitude={selected.lat}
      onClose={onClose}
      closeButton={false}
      closeOnClick={false}
      style={{ minWidth: 300 }}
    >
      <Card sx={{ border: "none", boxShadow: "none" }}>
        <CardContent sx={{ m: 0, p: 0 }}>
          <Box sx={{ textAlign: "center", borderBottom: 1, mb: 0.5 }}>
            <Typography component="div" sx={{ fontSize: 14 }}>
              {title}
            </Typography>
          </Box>
          {children}
        </CardContent>
        <CardActions sx={{ m: 0, p: 0 }}>
          <Button size="small" onClick={onClose}>
            Close
          </Button>
        </CardActions>
      </Card>
    </Popup>
  );
};

const StepItem = ({ title, tooltip, disableTooltip = false, children }) => (
  <ListItem sx={{ display: "block", pb: 0 }}>
    <Typography component="div" variant="subtitle1" sx={{ fontWeight: "bold" }}>
      {tooltip ? (
        <Tooltip
          title={<Typography sx={{ fontSize: 15 }}>{tooltip}</Typography>}
          placement="right-start"
          disableHoverListener={disableTooltip}
          disableFocusListener={disableTooltip}
          componentsProps={DARK_TOOLTIP_PROPS}
        >
          <span>
            {title} <InfoOutlinedIcon sx={{ fontSize: 15, mb: 1 }} />
          </span>
        </Tooltip>
      ) : (
        title
      )}
    </Typography>
    {children}
  </ListItem>
);

/* ------------------------------------------------------------------ */
/* Main component                                                      */
/* ------------------------------------------------------------------ */

const CommodityCorridors = () => {
  const mapRef = useRef(null);
  const railChartRef = useRef(null);
  const airportChartRef = useRef(null);
  const countrywideChartRef = useRef(null);

  const [corridorFeatures, setCorridorFeatures] = useState(null);
  const [commodityDetails, setCommodityDetails] = useState(() => buildCommodityDetails([]));
  const [commodityGroup, setCommodityGroup] = useState("All Commodities");
  const [mapParameters, setMapParameters] = useState(INITIAL_MAP_PARAMETERS);

  const [hoverInfo, setHoverInfo] = useState(null); // corridor tooltip
  const [hoverPopup, setHoverPopup] = useState(null); // point-layer hover label
  const [selected, setSelected] = useState(null); // clicked point feature

  const isCountrywide = mapParameters.showCountrywideAirportDelay === "visible";

  /* ---------- Data loading ---------- */

  useEffect(() => {
    let cancelled = false;
    fetchCorridorFeatures().then((features) => {
      if (cancelled) return;
      setCorridorFeatures(features);
      setCommodityDetails(buildCommodityDetails(features));
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const cargoAirportsData = useLazyGeoJson(
    CARGO_AIRPORTS_URL,
    mapParameters.showCargoAirports === "visible"
  );
  const portsData = useLazyGeoJson(PORTS_URL, mapParameters.showPorts === "visible");
  const truckParkingData = useLazyGeoJson(
    TRUCK_PARKING_URL,
    mapParameters.showTruckParking === "visible"
  );

  /* ---------- Derived map data and styles ---------- */

  const commodityInfo = useMemo(
    () => commodityDetails.find((c) => c.name === commodityGroup),
    [commodityDetails, commodityGroup]
  );

  const mapFeatures = useMemo(
    () =>
      corridorFeatures && {
        type: "FeatureCollection",
        features: corridorFeatures.filter(
          (f) => f.properties[mapParameters.commodities] > 0
        ),
      },
    [corridorFeatures, mapParameters.commodities]
  );

  const corridorLayerStyle = useMemo(() => {
    const {
      roadwayPerformance,
      commodities: commodityField,
      selectedCommoditiesCategory,
      colorScheme,
      barColor,
      barHeight,
      barOpacity,
      showRoads,
    } = mapParameters;
    const commodityKey = selectedCommoditiesCategory === "Route_valu" ? "values" : "tons";

    const ranges = {
      commodities: {
        field: commodityField,
        min: commodityInfo.minCommodity[commodityKey],
        max: commodityInfo.maxCommodity[commodityKey],
      },
      roadwayPerformance: {
        field: roadwayPerformance,
        min: commodityInfo.minRoadPerf[roadwayPerformance],
        max: commodityInfo.maxRoadPerf[roadwayPerformance],
      },
    };
    const color = safeRange(ranges[barColor]);
    const height = safeRange(ranges[barHeight]);

    return {
      id: "comm-corridor-layer",
      type: "fill-extrusion",
      layout: { visibility: showRoads },
      paint: {
        "fill-extrusion-color": [
          "interpolate", ["linear"], ["get", color.field],
          color.min, ["to-color", colorScheme[0]],
          (color.min + color.max) / 2, ["to-color", colorScheme[1]],
          color.max, ["to-color", colorScheme[2]],
        ],
        "fill-extrusion-height": [
          "interpolate", ["linear"], ["get", height.field],
          height.min, 1,
          height.max, 50000,
        ],
        "fill-extrusion-base": 0,
        "fill-extrusion-opacity": barOpacity,
      },
    };
  }, [mapParameters, commodityInfo]);

  /* ---------- Countrywide mode ---------- */

  // Delay lines follow the countrywide layer toggle
  useEffect(() => {
    setMapParameters((prev) => ({
      ...prev,
      showDelayLines: isCountrywide ? "visible" : "none",
    }));
  }, [isCountrywide]);

  useEffect(() => {
    const { longitude, latitude, zoom, pitch } = isCountrywide ? US_VIEW : TEXAS_VIEW;
    mapRef.current?.flyTo({ center: [longitude, latitude], zoom, pitch, duration: 2000 });
  }, [isCountrywide]);

  /* ---------- Icon images ---------- */

  const handleStyleImageMissing = useCallback((e) => {
    const map = mapRef.current?.getMap();
    const config = POINT_LAYERS.find((layer) => layer.imageId === e.id);
    if (!map || !config || map.hasImage(e.id)) return;

    const img = new Image(config.size, config.size);
    img.onload = () => {
      if (!map.hasImage(e.id)) map.addImage(e.id, img);
    };
    img.src = config.icon;
  }, []);

  const onMapLoad = useCallback(
    (e) => e.target.on("styleimagemissing", handleStyleImageMissing),
    [handleStyleImageMissing]
  );

  /* ---------- Control handlers ---------- */

  const handleCommodityGroupChange = (event) => {
    const group = event.target.value;
    setCommodityGroup(group);
    setMapParameters((prev) => ({
      ...prev,
      commodities: getCommodityField(group, prev.selectedCommoditiesCategory),
    }));
  };

  const handleCommodityChange = (event) => {
    const category = event.target.value;
    setMapParameters((prev) => ({
      ...prev,
      selectedCommoditiesCategory: category,
      commodities: getCommodityField(commodityGroup, category),
    }));
  };

  const handleRoadwayPerformanceChange = (event) => {
    const roadwayPerformance = event.target.value;
    setMapParameters((prev) => ({ ...prev, roadwayPerformance }));
  };

  /* ---------- Map mouse handlers ---------- */

  const onMouseMove = useCallback((event) => {
    const feature = event.features?.[0];
    if (feature?.source === "my-data") {
      setHoverInfo({ feature, x: event.point.x, y: event.point.y });
    } else {
      setHoverInfo(null);
    }
  }, []);

  const onMouseEnter = useCallback((event) => {
    const feature = event.features?.[0];
    const label = feature && POPUP_SOURCES[feature.source];
    if (label) {
      setHoverPopup({
        longitude: event.lngLat.lng,
        latitude: event.lngLat.lat,
        text: `${label} – ${feature.properties.name}`,
      });
    }
  }, []);

  const onMouseLeave = useCallback(() => {
    setHoverPopup(null);
    setHoverInfo(null);
  }, []);

  const onClick = useCallback((event) => {
    setHoverPopup(null);
    const feature = event.features?.[0];
    if (feature && POPUP_SOURCES[feature.source]) {
      setSelected({
        source: feature.source,
        properties: feature.properties,
        lng: event.lngLat.lng,
        lat: event.lngLat.lat,
      });
    } else {
      setSelected(null);
    }
  }, []);

  const closeSelected = () => setSelected(null);

  /* ---------- Detail popups ---------- */

  const renderSelectedPopup = () => {
    if (!selected) return null;
    const p = selected.properties;

    if (selected.source === "railDwellTime") {
      return (
        <DetailPopup
          selected={selected}
          title="Rail Dwell Times"
          onClose={closeSelected}
          chartRef={railChartRef}
        >
          <DetailRow label="Name">{p.name}</DetailRow>
          <DetailRow label="Average Dwell Time">
            {p.averageTerminalDwellTimeHours} hours
          </DetailRow>
          <Typography variant="caption" component="p" sx={{ m: 1, lineHeight: 1.3 }}>
            Average terminal dwell time, excluding cars on run-through trains.
            Measured in hours, based on railcars processed.
          </Typography>
          <GroupedBarChart
            chartHeight="250px"
            titleText="Dwell Time by Week"
            series={getRailSeries(p.id)}
            chartRef={railChartRef}
            xAxisCategories={RAIL_X_CATEGORIES}
            yAxisSettings={{
              allowDecimals: false,
              min: 15,
              tickInterval: 5,
              title: { text: "Terminal Dwell Time (hours)" },
            }}
            tooltipXlabel="Terminal Dwell Time (hours)"
            tooltipYlabel="Week Ending:"
            plotOptions={{ series: { type: "line" } }}
            chartSpacing={[5, 5, 5, 5]}
            chartType="line"
          />
        </DetailPopup>
      );
    }

    if (selected.source === "airportDelay") {
      const { categories, series } = getAirportDelayChart(p.id);
      return (
        <DetailPopup
          selected={selected}
          title="Airport Delay"
          onClose={closeSelected}
          chartRef={airportChartRef}
        >
          <DetailRow label="Name">{p.name}</DetailRow>
          <DetailRow label="Average Arrivals Delay">
            {p.averageArrrivalsDelayMinutes} minutes
          </DetailRow>
          <DetailRow label="Average Departures Delay">
            {p.averageDeparturesDelayMinutes} minutes
          </DetailRow>
          <Box sx={{ mt: 2 }}>
            <GroupedBarChart
              chartHeight="250px"
              titleText="Delay by Month"
              series={series}
              chartRef={airportChartRef}
              xAxisCategories={categories}
              yAxisSettings={{
                allowDecimals: false,
                min: 0,
                tickInterval: 10,
                title: { text: "Delay (minutes)" },
              }}
              tooltipXlabel="Delay"
              tooltipYlabel="minutes"
              plotOptions={{ series: { pointWidth: 25 } }}
              chartSpacing={[5, 10, 5, 5]}
              chartType="column"
            />
          </Box>
        </DetailPopup>
      );
    }

    if (selected.source === "countrywideAirportDelay") {
      return (
        <DetailPopup
          selected={selected}
          title="Countrywide Airport Delay"
          onClose={closeSelected}
          chartRef={countrywideChartRef}
        >
          <DetailRow label="Name">{p.name}</DetailRow>
          <DetailRow label="Average Arrivals Delay">
            {p.avgArrivalsDelay} minutes (line color)
          </DetailRow>
          <DetailRow label="Average Departures Delay">
            {p.avgDeparturesDelay} minutes (line width)
          </DetailRow>
          <PieChart
            arrivals={p.avgArrivalsDelay}
            departures={p.avgDeparturesDelay}
            chartRef={countrywideChartRef}
          />
        </DetailPopup>
      );
    }

    return null;
  };

  /* ---------- Render ---------- */

  return (
    <Grid container spacing={0} className="commodity-corridors">
      <Grid item xs={8}>
        <Map
          ref={mapRef}
          onLoad={onMapLoad}
          initialViewState={TEXAS_VIEW}
          style={{ width: "65vw", height: "87vh" }}
          mapStyle={`mapbox://styles/${mapParameters.baseMap}`}
          mapboxAccessToken={process.env.REACT_APP_MAPBOX_ACCESSTOKEN}
          onMouseMove={onMouseMove}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          onClick={onClick}
          interactiveLayerIds={INTERACTIVE_LAYER_IDS}
          attributionControl={false}
        >
          {hoverPopup && (
            <Popup
              longitude={hoverPopup.longitude}
              latitude={hoverPopup.latitude}
              closeButton={false}
              closeOnClick={false}
              className="county-info"
            >
              <Typography sx={{ fontSize: 14 }} color="text.secondary">
                {hoverPopup.text}
              </Typography>
              <Typography sx={{ fontSize: 14 }} color="text.secondary">
                (Click for more information)
              </Typography>
            </Popup>
          )}

          {renderSelectedPopup()}

          {/* Commodity corridors */}
          <Source id="my-data" type="geojson" data={mapFeatures ?? EMPTY_FC}>
            <Layer {...corridorLayerStyle} />
          </Source>
          <RouteTooltip hoverInfo={hoverInfo} mapRef={mapRef} />

          {/* AIS vessel transits */}
          {mapParameters.showAisData2021 === "visible" && (
            <Source id="ais-2021-data" type="raster" tiles={[AIS_TILES_URL]} tileSize={128}>
              <Layer {...AIS_LAYER} />
            </Source>
          )}

          {/* Point layers */}
          <Source id="cargo-airports" type="geojson" data={cargoAirportsData ?? EMPTY_FC}>
            <Layer {...symbolLayer("cargo-airports-layer", "pin", mapParameters.showCargoAirports)} />
          </Source>
          <Source id="ports" type="geojson" data={portsData ?? EMPTY_FC}>
            <Layer {...symbolLayer("ports-layer", "ports", mapParameters.showPorts)} />
          </Source>
          <Source id="truckParking" type="geojson" data={truckParkingData ?? EMPTY_FC}>
            <Layer
              {...symbolLayer("truckParking-layer", "truckParking", mapParameters.showTruckParking, 1, true)}
            />
          </Source>
          <Source id="railDwellTime" type="geojson" data={RAIL_DWELL_DATA}>
            <Layer
              {...symbolLayer("railDwellTimes-layer", "railDwellTImes", mapParameters.showRailDwellTimes, 1.5, true)}
            />
          </Source>
          <Source id="airportDelay" type="geojson" data={AIRPORT_DELAY_DATA}>
            <Layer
              {...symbolLayer("airportDelay-layer", "airportDelay", mapParameters.showAirportDelay, 1.5, true)}
            />
          </Source>
          <Source id="countrywideAirportDelay" type="geojson" data={COUNTRYWIDE_AIRPORT_DATA}>
            <Layer
              {...symbolLayer(
                "countrywideAirportDelay-layer",
                "countrywideAirportDelay",
                mapParameters.showCountrywideAirportDelay,
                1.5
              )}
            />
          </Source>
          {mapParameters.showDelayLines === "visible" && (
            <Source id="delay-lines" type="geojson" data={DELAY_LINES_DATA}>
              <Layer {...DELAY_LINES_LAYER} beforeId="countrywideAirportDelay-layer" />
            </Source>
          )}

          {!mapFeatures && (
            <Box sx={{ display: "flex", mt: "25vh", ml: "25vw" }}>
              <CircularProgress size={75} thickness={6} />
            </Box>
          )}

          {mapFeatures && (
            <>
              <MapLegend mapParameters={mapParameters} commodityInfo={commodityInfo} />
              <LayerControls mapParameters={mapParameters} setMapParameters={setMapParameters} />
            </>
          )}

          <NavigationControl className="tooltip" />
          <MapSettings mapParameters={mapParameters} setMapParameters={setMapParameters} />
        </Map>
      </Grid>

      <Grid item xs={4}>
        <Box sx={{ pt: 1 }}>
          <Typography component="div">
            The map to the left provides a visual representation of both commodities
            and roadway performance. The commodity information (either tons or dollar value)
            is shown using the height of the bars, with taller bars indicating higher values
            and shorter bars indicating lower values. The roadway performance
            (either annual truck delay or reliability using the planning time index, PTI 95)
            is shown by color, with red being the maximum value, yellow being the median value,
            and green being the lower values..
          </Typography>

          <List sx={{ width: "100%", bgcolor: "background.paper", py: 0 }}>
            <StepItem title="1. Choose the commodity group.">
              <FormControl variant="standard" fullWidth sx={{ mt: 1, maxWidth: 250 }}>
                <Select
                  id="commodity-group-select"
                  value={commodityGroup}
                  onChange={handleCommodityGroupChange}
                >
                  {commodities.map((item) => (
                    <MenuItem key={item.name} value={item.name}>
                      <Tooltip
                        title={<Typography sx={{ fontSize: 15 }}>{item.tooltip}</Typography>}
                        placement="right-start"
                        componentsProps={{
                          tooltip: {
                            sx: {
                              ...DARK_TOOLTIP_PROPS.tooltip.sx,
                              marginLeft: "75px !important",
                            },
                          },
                        }}
                      >
                        <div>{item.name}</div>
                      </Tooltip>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </StepItem>

            <StepItem
              title="2. Choose the commodity variable."
              tooltip="The commodity is shown on the map by either tonnage or value."
              disableTooltip={
                mapParameters.barColor !== "commodities" &&
                mapParameters.barHeight !== "commodities"
              }
            >
              <FormControl fullWidth>
                <RadioGroup
                  row
                  name="commodities-radio-buttons-group"
                  value={mapParameters.selectedCommoditiesCategory}
                  onChange={handleCommodityChange}
                >
                  <FormControlLabel value="Route_tons" control={<Radio />} label="Tonnage" />
                  <FormControlLabel value="Route_valu" control={<Radio />} label="Value" />
                </RadioGroup>
              </FormControl>
            </StepItem>

            <StepItem
              title="3. Choose the roadway performance variable."
              tooltip="Roadway performance is shown on the map by either truck delay or reliability."
            >
              <FormControl fullWidth>
                <RadioGroup
                  row
                  name="roadway-performance-radio-buttons-group"
                  value={mapParameters.roadwayPerformance}
                  onChange={handleRoadwayPerformanceChange}
                >
                  <FormControlLabel value="trkdelay" control={<Radio />} label="Truck Delay" />
                  <FormControlLabel value="trkpti95" control={<Radio />} label="Reliability (PTI-95)" />
                </RadioGroup>
              </FormControl>
            </StepItem>
          </List>
        </Box>
      </Grid>
    </Grid>
  );
};

export default CommodityCorridors;